import type { BaseUserRole, InstituteRoleMap, UserRole } from '@/types'

const ROLE_PRIORITY: Record<Exclude<UserRole, 'user'>, number> = {
  superuser: 0,
  manager: 1,
  tutor: 2,
  student: 3,
}

const VALID_ROLES = new Set<Exclude<UserRole, 'user'>>(['superuser', 'manager', 'tutor', 'student'])

/** Normalizes the persisted base role claim. */
export function normalizeBaseRole(value: unknown): BaseUserRole {
  return value === 'superuser' ? 'superuser' : 'user'
}

/**
 * Normalizes unknown claim data into an InstituteRoleMap.
 * Invalid entries are dropped.
 */
export function normalizeInstituteRoles(value: unknown): InstituteRoleMap {
  if (!value || typeof value !== 'object') return {}

  const instituteRoles: InstituteRoleMap = {}

  Object.entries(value as Record<string, unknown>).forEach(([instituteId, role]) => {
    if (instituteId && typeof role === 'string' && VALID_ROLES.has(role as Exclude<UserRole, 'user'>)) {
      instituteRoles[instituteId] = role as Exclude<UserRole, 'user'>
    }
  })

  return instituteRoles
}

/** Returns institute IDs available in the role map, excluding the global key. */
export function getMemberInstitutes(instituteRoles?: InstituteRoleMap | null): string[] {
  if (!instituteRoles) return []
  return Object.keys(instituteRoles).filter((instituteId) => instituteId !== 'global')
}

/** Returns the highest-priority role found in a role map, or null when empty. */
export function getHighestRole(instituteRoles: InstituteRoleMap): UserRole | null {
  const roles = Object.values(instituteRoles).filter((role): role is Exclude<UserRole, 'user'> => Boolean(role))
  if (roles.length === 0) return null

  return roles.reduce<Exclude<UserRole, 'user'>>((highest, current) => {
    return ROLE_PRIORITY[current] < ROLE_PRIORITY[highest] ? current : highest
  }, roles[0])
}

/** Resolves effective runtime role from base claim plus institute role map. */
export function getEffectiveUserRole(instituteRoles: InstituteRoleMap, baseRole: BaseUserRole): UserRole {
  return getHighestRole(instituteRoles) ?? baseRole
}

/**
 * Resolves effective institute scope for queries.
 * Returns undefined for superuser global scope, otherwise selected/first institute.
 */
export function getScopedInstitutes(
  instituteRoles: InstituteRoleMap,
  selectedInstituteId: string | null,
  isSuperUser: boolean,
): string[] | undefined {
  if (isSuperUser && (!selectedInstituteId || selectedInstituteId === 'global')) {
    return undefined
  }

  if (selectedInstituteId) {
    return [selectedInstituteId]
  }

  const institutes = getMemberInstitutes(instituteRoles)
  return institutes.length > 0 ? [institutes[0]] : []
}